import { inject } from '@angular/core';
import { TranslatorService } from '@sc/data-access-translation';
import { EMPTY, of } from 'rxjs';
import { expand, filter, map, switchMap } from 'rxjs/operators';

export class ContextualDisplayService<T extends string> {
  protected translator = inject(TranslatorService);

  protected defaultKeys: {
    stable: string[];
    variable: T[]; // Variable keys are sorted from least to highest priorty
  };

  constructor(stableTradKeys: string[], ...defaultTradKeys: T[]) {
    this.defaultKeys = {
      stable: stableTradKeys || [],
      variable: defaultTradKeys || [],
    };
  }

  protected buildPossibleKeys(
    baseKey: string,
    defaultKeys: T[],
    alternativeKeys: string[]
  ): string[] {
    if (!defaultKeys?.length || !alternativeKeys?.length) {
      return [baseKey];
    }

    return [
      ...this.buildPossibleKeys(
        `${alternativeKeys[alternativeKeys.length - 1]}${
          baseKey ? `.${baseKey}` : ''
        }`,
        defaultKeys.slice(0, -1),
        alternativeKeys.slice(0, -1)
      ),
      ...this.buildPossibleKeys(
        `${defaultKeys[defaultKeys.length - 1]}${baseKey ? `.${baseKey}` : ''}`,
        defaultKeys.slice(0, -1),
        alternativeKeys.slice(0, -1)
      ),
    ];
  }

  protected getPossibleKeysList(...alternativeKeys: string[]) {
    const baseKey = this.defaultKeys.stable.join('.');

    const possibleKeys: string[] = this.buildPossibleKeys(
      '',
      this.defaultKeys.variable,
      alternativeKeys
    );

    return possibleKeys.map((key) => `${baseKey}.${key}`);
  }

  protected getInstantContextualKey(...alternativeKeys: string[]) {
    const allPossibleKeys = this.getPossibleKeysList(...alternativeKeys);

    return (
      allPossibleKeys.find((key) => this.translator.instantIsKeyDefined(key)) ||
      ''
    );
  }

  protected getContextualKey(...alternativeKeys: string[]) {
    const allPossibleKeys = this.getPossibleKeysList(...alternativeKeys);

    return of(0).pipe(
      expand((keyIndex: number | string) => {
        if (keyIndex === -1 || typeof keyIndex === 'string') {
          return EMPTY;
        }

        if (keyIndex >= allPossibleKeys.length) {
          return of(-1);
        }

        return this.translator
          .isKeyDefined(allPossibleKeys[keyIndex])
          .pipe(
            map((isDefined) =>
              isDefined ? allPossibleKeys[keyIndex] : keyIndex + 1
            )
          );
      }),
      filter((keyIndex) => keyIndex === -1 || typeof keyIndex === 'string'),
      map((keyIndex) => (keyIndex === -1 ? '' : (keyIndex as string)))
    );
  }

  protected getInstantContextualTranslation(
    alternativeKeys: string[],
    interpolateParams?: any
  ) {
    const key = this.getInstantContextualKey(...alternativeKeys);

    return this.translator.getInstantTranslation(key, interpolateParams);
  }

  protected getContextualTranslation(
    alternativeKeys: string[],
    interpolateParams?: any
  ) {
    return this.getContextualKey(...alternativeKeys).pipe(
      switchMap((key) => this.translator.getTranslation(key, interpolateParams))
    );
  }
}
